import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useState, useEffect } from "react";
import { queryClient } from "@/lib/queryClient";
import { AlertTriangle, Shield, Eye, Tag, Globe, ExternalLink } from "lucide-react";

export function AlertsTimeline() {
  const { data: alerts, isLoading, error, refetch } = useQuery({
    queryKey: ["/api/alerts"],
    refetchInterval: 10000, // Refresh every 10 seconds
    staleTime: 0, // Override global staleTime
    refetchOnMount: true, // Force refetch on mount
    refetchOnReconnect: true, // Refetch on reconnect
  });

  // Force invalidate and refetch on component mount
  useEffect(() => {
    // Clear all cache and force fresh fetch
    queryClient.removeQueries({ queryKey: ["/api/alerts"] });
    setTimeout(() => {
      refetch();
    }, 100);
  }, [refetch]);

  // Debug logging
  console.log('Alerts Data:', {
    alerts,
    alertsLength: Array.isArray(alerts) ? alerts.length : 0,
    isLoading,
    error
  });

  const [selectedAlert, setSelectedAlert] = useState<any>(null);

  // Removed WebSocket dependency for laptop deployment
  // Data will refresh via React Query's refetchInterval

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "critical": return "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400";
      case "high": return "bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-400";
      case "medium": return "bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-400";
      case "low": return "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400";
      default: return "bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-400";
    }
  };

  const getBorderColor = (severity: string) => {
    switch (severity) {
      case "critical": return "border-l-red-500";
      case "high": return "border-l-orange-500";
      case "medium": return "border-l-amber-500";
      case "low": return "border-l-yellow-500";
      default: return "border-l-gray-500";
    }
  };

  const getIcon = (type: string) => {
    switch (type) {
      case "security": return AlertTriangle;
      case "tracking": return Eye;
      case "domain": return Globe;
      case "tag": return Tag;
      default: return Shield;
    }
  };

  const extractDomain = (description: string) => {
    const domainMatch = description.match(/\b([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}\b/);
    return domainMatch ? domainMatch[0] : null;
  };

  const getTimeAgo = (date: string) => {
    const alertDate = new Date(date);
    const now = new Date();
    const diff = now.getTime() - alertDate.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) return "Today";
    if (days === 1) return "1 day ago";
    return `${days} days ago`;
  };

  return (
    <Card>
      <CardHeader>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
          Alerts Timeline
        </h3>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="flex items-start space-x-3 animate-pulse">
                <div className="w-2 h-2 bg-slate-200 dark:bg-slate-700 rounded-full mt-2"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-16"></div>
                  <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-48"></div>
                  <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-32"></div>
                </div>
                <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-16"></div>
              </div>
            ))}
          </div>
        ) : !alerts || (alerts as any[]).length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Shield className="h-12 w-12 mx-auto mb-4 text-slate-300" />
            <div className="text-lg font-medium mb-2">No security alerts yet</div>
            <div className="text-sm space-y-2">
              <div>Scan a domain to generate security alerts</div>
              <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <div className="text-blue-700 dark:text-blue-300 text-sm space-y-1">
                  <div className="font-medium">⚡ Alerts are automatically created when scans detect security issues like missing headers, suspicious scripts, or compliance violations.</div>
                  <div>• Go to Domain Scanner and scan any website</div>
                  <div>• Security alerts will appear here automatically after scanning</div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {(alerts as any[])?.slice(0, 6).map((alert: any) => {
              const Icon = getIcon(alert.type);
              const domain = extractDomain(alert.description);
              
              return (
                <Dialog key={alert.id}>
                  <DialogTrigger asChild>
                    <div 
                      className={`bg-white dark:bg-slate-800 border-l-4 ${getBorderColor(alert.severity)} rounded-r-lg p-4 shadow-sm hover:shadow-md transition-all cursor-pointer hover:scale-[1.02] hover:bg-slate-50 dark:hover:bg-slate-700/50`}
                      data-testid={`alert-item-${alert.id}`}
                    >
                      <div className="flex items-start space-x-3">
                        <div className={`p-2 rounded-lg ${getSeverityColor(alert.severity)}`}>
                          <Icon className="h-4 w-4" />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className={`${getSeverityColor(alert.severity)} text-xs px-2 py-1 rounded-md font-medium`}>
                              {alert.severity.charAt(0).toUpperCase() + alert.severity.slice(1)}
                            </Badge>
                            <span className="text-xs text-slate-500 dark:text-slate-400">
                              • {getTimeAgo(alert.createdAt)}
                            </span>
                          </div>
                          
                          <div className="space-y-1">
                            {domain && (
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-xs text-slate-600 dark:text-slate-400">The domain</span>
                                <Badge 
                                  variant="secondary" 
                                  className="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 rounded-full"
                                >
                                  {domain}
                                </Badge>
                              </div>
                            )}
                            
                            <p className="text-sm font-medium text-slate-900 dark:text-white leading-relaxed">
                              {alert.description}
                            </p>
                          </div>
                        </div>

                        <div className="flex-shrink-0">
                          <ExternalLink className="h-4 w-4 text-slate-400 dark:text-slate-500" />
                        </div>
                      </div>
                    </div>
                  </DialogTrigger>
                  
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <Icon className="h-5 w-5" />
                        Security Alert Details
                      </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4">
                      <div className="flex items-center gap-2">
                        <Badge className={`${getSeverityColor(alert.severity)} px-3 py-1 rounded-md font-medium`}>
                          {alert.severity.charAt(0).toUpperCase() + alert.severity.slice(1)} Severity
                        </Badge>
                        <Badge variant="outline" className="px-3 py-1">
                          {alert.type.charAt(0).toUpperCase() + alert.type.slice(1)} Alert
                        </Badge>
                      </div>
                      
                      {domain && (
                        <div className="bg-slate-50 dark:bg-slate-800 p-3 rounded-lg">
                          <div className="text-sm text-slate-600 dark:text-slate-400 mb-1">Affected Domain:</div>
                          <div className="font-medium text-slate-900 dark:text-white">{domain}</div>
                        </div>
                      )}
                      
                      <div className="bg-slate-50 dark:bg-slate-800 p-3 rounded-lg">
                        <div className="text-sm text-slate-600 dark:text-slate-400 mb-1">Description:</div>
                        <div className="font-medium text-slate-900 dark:text-white">{alert.description}</div>
                      </div>
                      
                      <div className="bg-slate-50 dark:bg-slate-800 p-3 rounded-lg">
                        <div className="text-sm text-slate-600 dark:text-slate-400 mb-1">Detected:</div>
                        <div className="font-medium text-slate-900 dark:text-white">{getTimeAgo(alert.createdAt)}</div>
                      </div>
                      
                      {alert.type === 'tracking' && (
                        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3 rounded-lg">
                          <div className="text-sm text-amber-700 dark:text-amber-300 font-medium mb-1">Recommendation:</div>
                          <div className="text-sm text-amber-600 dark:text-amber-400">
                            Review your privacy policy and consider implementing stricter tracking controls.
                          </div>
                        </div>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
              );
            })}
            
            {(!alerts || (alerts as any[]).length === 0) && (
              <div className="text-center py-8">
                <Shield className="h-12 w-12 text-slate-400 dark:text-slate-600 mx-auto mb-3" />
                <div className="space-y-2">
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    No security alerts yet
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Scan a domain to generate security alerts
                  </p>
                  <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <p className="text-xs text-blue-700 dark:text-blue-300">
                      💡 Alerts are automatically created when scans detect security issues like missing headers, suspicious scripts, or compliance violations.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
